import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Clock, Sparkles, Zap, BookOpen, Gem, Headphones, CheckCircle2 } from 'lucide-react';
interface ShopUpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
const UPSELL_IMG = 'https://cdn.shopify.com/s/files/1/0981/0781/8286/files/Libro_dorado_comprimida.png?v=1775619938&width=400';
export function ShopUpsellPopup({
  isOpen,
  onClose
}: ShopUpsellPopupProps) {
  const [timeLeft, setTimeLeft] = useState(300);
  useEffect(() => {
    if (!isOpen) {
      setTimeLeft(300);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const handleAccept = () => {
    window.location.href = 'https://betterdailyguides.com/cart/51956021231918:1';
  };
  const handleDecline = () => {
    window.location.href = 'https://www.betterdailyguides.com/cart/51937703297326:1';
  };
  const popupMotion = {
    initial: {
      opacity: 0,
      scale: 0.85,
      y: 30
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: -20
    },
    transition: {
      type: 'spring' as const,
      damping: 22,
      stiffness: 280
    }
  };
  return <AnimatePresence mode="wait" data-id="element-169">
      <div className="fixed inset-0 z-[100000] flex items-center justify-center p-2 sm:p-6 md:p-8 overflow-y-auto" data-id="element-170">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/92 backdrop-blur-md" data-id="element-171" />

        <motion.div key="shop_upsell" {...popupMotion} className="relative w-full max-w-[380px] sm:max-w-[440px] md:max-w-lg z-10 rounded-2xl sm:rounded-3xl overflow-hidden my-auto" style={{
        boxShadow: '0 0 100px rgba(212,175,55,0.35), 0 30px 80px rgba(0,0,0,0.7)'
      }} data-id="element-172">
          <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-gradient-to-b from-gold via-gold/40 to-gold/60 p-[2px]" data-id="element-173">
            <div className="w-full h-full rounded-2xl sm:rounded-3xl bg-[#070707]" data-id="element-174" />
          </div>
          <div className="relative z-10" data-id="element-175">
            {/* Header with countdown */}
            <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white text-center py-2 sm:py-2.5 px-3" data-id="element-176">
              <div className="font-black text-[11px] sm:text-sm uppercase tracking-wider animate-pulse" data-id="element-177">
                ⚡ ESPERA — OFERTA ÚNICA 20% OFF ⚡
              </div>
              <div className="flex items-center justify-center gap-1.5 mt-1" data-id="element-178">
                <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-179" />
                <span className="font-mono font-black text-xs sm:text-sm tracking-wider" data-id="element-180">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>

            {/* BG image */}
            <div className="relative min-h-[80px] sm:min-h-[120px] flex items-end justify-center overflow-hidden" data-id="element-181">
              <img src={UPSELL_IMG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center opacity-20 scale-110 blur-[1px]" data-id="element-182" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/80 to-[#070707]/30" data-id="element-183" />
              <div className="absolute inset-0 opacity-25" style={{
              background: 'radial-gradient(ellipse at 50% 50%, rgba(212,175,55,0.3) 0%, transparent 65%)'
            }} data-id="element-184" />
              <div className="relative z-10 text-center px-4 pb-2 pt-4 sm:px-5 sm:pb-3 sm:pt-5" data-id="element-185">
                <h3 className="font-serif text-lg sm:text-2xl font-black text-white leading-tight drop-shadow-lg" data-id="element-186">
                  Llévate el Paquete <span className="text-gold" data-id="element-187">Completo</span>
                </h3>
              </div>
            </div>

            {/* Package contents */}
            <div className="px-3 sm:px-5 pt-2 sm:pt-3 pb-1" data-id="element-188">
              <p className="text-white/40 text-[9px] sm:text-[10px] uppercase tracking-[2px] font-bold text-center mb-2 sm:mb-3" data-id="element-189">
                Todo lo que incluye
              </p>
              <div className="space-y-1.5 sm:space-y-2.5" data-id="element-190">
                <div className="flex items-center gap-2.5 sm:gap-3 bg-gold/8 border border-gold/15 rounded-lg sm:rounded-xl px-2.5 sm:px-3 py-2 sm:py-2.5" data-id="element-191">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-gold shrink-0" data-id="element-192" />
                  <span className="text-white font-bold text-[11px] sm:text-sm" data-id="element-193">
                    Bono 1: The Flow Map 888
                  </span>
                </div>
                <div className="flex items-center gap-2.5 sm:gap-3 bg-gold/8 border border-gold/15 rounded-lg sm:rounded-xl px-2.5 sm:px-3 py-2 sm:py-2.5" data-id="element-194">
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-gold shrink-0" data-id="element-195" />
                  <span className="text-white font-bold text-[11px] sm:text-sm" data-id="element-196">
                    Bono 2: La Llave Maestra
                  </span>
                </div>
                <div className="flex items-center gap-2.5 sm:gap-3 bg-gold/8 border border-gold/15 rounded-lg sm:rounded-xl px-2.5 sm:px-3 py-2 sm:py-2.5" data-id="element-197">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-gold shrink-0" data-id="element-198" />
                  <span className="text-white font-bold text-[11px] sm:text-sm" data-id="element-199">
                    Bono 3: Manifestación Acelerada
                  </span>
                </div>
                <div className="flex items-center gap-2.5 sm:gap-3 bg-gold/8 border border-gold/15 rounded-lg sm:rounded-xl px-2.5 sm:px-3 py-2 sm:py-2.5" data-id="element-200">
                  <Gem className="w-4 h-4 sm:w-5 sm:h-5 text-gold shrink-0" data-id="element-201" />
                  <span className="text-white font-bold text-[11px] sm:text-sm" data-id="element-202">
                    Bono 4: Código de Riqueza Suprema
                  </span>
                </div>
                <div className="flex items-center gap-2.5 sm:gap-3 bg-gold/8 border border-gold/15 rounded-lg sm:rounded-xl px-2.5 sm:px-3 py-2 sm:py-2.5" data-id="element-203">
                  <Headphones className="w-4 h-4 sm:w-5 sm:h-5 text-gold shrink-0" data-id="element-204" />
                  <span className="text-white font-bold text-[11px] sm:text-sm" data-id="element-205">
                    Audios de Meditación Premium
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 mt-3 sm:mt-4 mb-1" data-id="element-206">
                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gold" data-id="element-207" />
                <span className="text-gold text-[9px] sm:text-xs font-bold uppercase tracking-wider" data-id="element-208">
                  Acceso inmediato y de por vida
                </span>
              </div>
            </div>

            {/* Price + CTA */}
            <div className="px-3 pb-3 sm:px-5 sm:pb-5 pt-2" data-id="element-209">
              <div className="text-center mb-2 sm:mb-3" data-id="element-210">
                <div className="inline-block bg-green-400/15 text-green-400 text-[9px] sm:text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider border border-green-400/20 mb-2" data-id="element-211">
                  20% DESCUENTO EXCLUSIVO
                </div>
                <div className="flex items-center justify-center gap-2 sm:gap-3" data-id="element-212">
                  <span className="text-lg sm:text-xl text-white/20 line-through font-bold" data-id="element-213">
                    $23.00
                  </span>
                  <span className="text-3xl sm:text-5xl font-black text-green-400 tracking-tight drop-shadow-[0_0_20px_rgba(74,222,128,0.3)]" data-id="element-214">
                    $18.40
                  </span>
                </div>
                <div className="text-white/35 text-[10px] sm:text-[11px] mt-1" data-id="element-215">
                  Ahorras $4.60 · Pago único
                </div>
              </div>
              <motion.button onClick={handleAccept} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.97
            }} className="w-full bg-gradient-to-r from-green-500 to-emerald-400 text-dark font-black text-sm sm:text-lg py-3.5 sm:py-5 rounded-xl sm:rounded-2xl shadow-[0_10px_40px_rgba(74,222,128,0.35)] flex items-center justify-center gap-2 uppercase tracking-wider" data-id="element-216">
                SÍ, QUIERO EL PAQUETE COMPLETO
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-217" />
              </motion.button>

              <button onClick={handleDecline} className="w-full text-center mt-3 sm:mt-4 text-white/20 hover:text-white/40 text-[10px] sm:text-[11px] underline transition-colors tracking-wide" data-id="element-218">
                No gracias, continuar solo con la Guía + 1 Bono por $12.78
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}